import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  Textarea,
} from '@insolech/ui';
import {
  MODIFICATIONS,
  type ModificationDefinition,
  type Region,
} from '@/features/orders/wizard/forms/modificationCatalog';
import { providerQueries, type ProviderLineDetail } from '@insolech/sdk';
import { supabase } from '@/lib/supabase';

/**
 * Opinionated 4-step modification suggestion flow:
 *
 *   1. Region (radio cards: rearfoot / midfoot / forefoot / offloading)
 *   2. Modification (filtered list pulled from the catalog)
 *   3. Suggested change — shows current value(s) side-by-side with editable
 *      proposed values for each parameter (number + select). For modifications
 *      with no parameters, the operator just sees an "intensity" picker.
 *   4. Reason — required free-text explanation
 *
 * Submits to `provider_suggest_modification` RPC, which inserts the row and
 * fans out a notification to the podiatrist.
 */

const REGIONS: ReadonlyArray<{ key: Region; labelKey: string }> = [
  { key: 'rearfoot', labelKey: 'provider:suggest_region_rearfoot' },
  { key: 'midfoot', labelKey: 'provider:suggest_region_midfoot' },
  { key: 'forefoot', labelKey: 'provider:suggest_region_forefoot' },
  { key: 'offloading', labelKey: 'provider:suggest_region_offloading' },
];

interface ExistingMod {
  region: Region;
  modification_type: string;
  intensity?: 'light' | 'medium' | 'strong';
  parameters?: Record<string, unknown>;
}

export function SuggestModificationDialog({
  open,
  onOpenChange,
  lineId,
  line,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lineId: string;
  line: ProviderLineDetail;
}) {
  const { t } = useTranslation(['provider', 'common']);
  const [step, setStep] = React.useState<1 | 2 | 3 | 4>(1);
  const [region, setRegion] = React.useState<Region | null>(null);
  const [modification, setModification] = React.useState<ModificationDefinition | null>(null);
  const [proposedIntensity, setProposedIntensity] = React.useState<
    'light' | 'medium' | 'strong' | undefined
  >(undefined);
  const [proposedParams, setProposedParams] = React.useState<Record<string, unknown>>({});
  const [reason, setReason] = React.useState('');
  const [submittedOk, setSubmittedOk] = React.useState(false);

  // Pull existing modification on this line so we can show "current" values.
  const existing = React.useMemo<ExistingMod | null>(() => {
    if (!modification) return null;
    const list = (line.modifications ?? []) as ExistingMod[];
    return (
      list.find(
        (m) => m.region === modification.region && m.modification_type === modification.id,
      ) ?? null
    );
  }, [line.modifications, modification]);

  React.useEffect(() => {
    if (!open) return;
    // Reset on each open
    setStep(1);
    setRegion(null);
    setModification(null);
    setProposedIntensity(undefined);
    setProposedParams({});
    setReason('');
    setSubmittedOk(false);
  }, [open]);

  // Pre-fill proposed values from existing whenever the modification is picked.
  React.useEffect(() => {
    if (!modification) return;
    setProposedIntensity(existing?.intensity);
    setProposedParams({ ...(existing?.parameters ?? {}) });
  }, [modification, existing]);

  const submit = useMutation({
    mutationFn: async () => {
      if (!modification) throw new Error('Pick a modification first.');
      if (!reason.trim()) throw new Error('Reason is required.');
      const fieldPath = `modifications.${modification.region}.${modification.id}`;
      const currentValue = existing
        ? {
            intensity: existing.intensity ?? null,
            parameters: existing.parameters ?? {},
          }
        : null;
      const proposedValue = {
        intensity: proposedIntensity ?? null,
        parameters: proposedParams,
      };
      await providerQueries.providerSuggestModification(supabase, {
        orderLineId: lineId,
        fieldPath,
        currentValue,
        proposedValue,
        rationale: reason.trim(),
      });
    },
    onSuccess: () => {
      setSubmittedOk(true);
    },
  });

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="lg">
        <ModalHeader>
          <ModalTitle>{t('provider:suggest_title')}</ModalTitle>
        </ModalHeader>
        <ModalBody className="space-y-6">
          {submittedOk ? (
            <Alert tone="success" title={t('provider:suggest_success')}>
              The podiatrist has been notified.
            </Alert>
          ) : (
            <>
              <Stepper step={step} />

              {step === 1 ? (
                <StepRegion
                  region={region}
                  onPick={(r) => {
                    setRegion(r);
                    setStep(2);
                  }}
                />
              ) : null}

              {step === 2 && region ? (
                <StepModification
                  region={region}
                  current={modification}
                  onPick={(m) => {
                    setModification(m);
                    setStep(3);
                  }}
                  onBack={() => setStep(1)}
                />
              ) : null}

              {step === 3 && modification ? (
                <StepChange
                  modification={modification}
                  existing={existing}
                  proposedIntensity={proposedIntensity}
                  setProposedIntensity={setProposedIntensity}
                  proposedParams={proposedParams}
                  setProposedParams={setProposedParams}
                  onBack={() => setStep(2)}
                  onNext={() => setStep(4)}
                />
              ) : null}

              {step === 4 && modification ? (
                <StepReason
                  reason={reason}
                  setReason={setReason}
                  onBack={() => setStep(3)}
                  onSubmit={() => submit.mutate()}
                  pending={submit.isPending}
                  error={submit.error instanceof Error ? submit.error.message : null}
                />
              ) : null}
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function Stepper({ step }: { step: 1 | 2 | 3 | 4 }) {
  const { t } = useTranslation(['provider']);
  const labels = [
    t('provider:suggest_step_region'),
    t('provider:suggest_step_modification'),
    t('provider:suggest_step_change'),
    t('provider:suggest_step_reason'),
  ];
  return (
    <ol className="flex items-center gap-2 text-xs">
      {labels.map((label, i) => {
        const stepNum = i + 1;
        const active = stepNum === step;
        const done = stepNum < step;
        return (
          <li
            key={label}
            className={`flex items-center gap-2 ${
              done
                ? 'text-success-700'
                : active
                  ? 'text-brand-700 font-medium'
                  : 'text-text-muted'
            }`}
          >
            <span
              className={`h-5 w-5 rounded-full inline-flex items-center justify-center text-[10px] ${
                active ? 'bg-brand-100' : done ? 'bg-success-100' : 'bg-sand-100'
              }`}
            >
              {stepNum}
            </span>
            <span>{label}</span>
            {stepNum < 4 ? <span className="text-text-muted">›</span> : null}
          </li>
        );
      })}
    </ol>
  );
}

function StepRegion({
  region,
  onPick,
}: {
  region: Region | null;
  onPick: (r: Region) => void;
}) {
  const { t } = useTranslation(['provider']);
  return (
    <div className="grid grid-cols-2 gap-3">
      {REGIONS.map((r) => (
        <button
          key={r.key}
          type="button"
          onClick={() => onPick(r.key)}
          data-testid={`provider-suggest-region-${r.key}`}
          className={`text-start rounded-md border px-4 py-3 hover:border-brand-500 hover:bg-brand-50/50 transition ${
            region === r.key ? 'border-brand-500 bg-brand-50' : 'border-border-subtle'
          }`}
        >
          <span className="text-sm font-medium text-text-primary">{t(r.labelKey)}</span>
        </button>
      ))}
    </div>
  );
}

function StepModification({
  region,
  current,
  onPick,
  onBack,
}: {
  region: Region;
  current: ModificationDefinition | null;
  onPick: (m: ModificationDefinition) => void;
  onBack: () => void;
}) {
  const list = MODIFICATIONS.filter((m) => m.region === region);
  const { t } = useTranslation(['provider']);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {list.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => onPick(m)}
            data-testid={`provider-suggest-mod-${m.id}`}
            className={`text-start rounded-md border px-3 py-2 hover:border-brand-500 hover:bg-brand-50/50 transition ${
              current?.id === m.id ? 'border-brand-500 bg-brand-50' : 'border-border-subtle'
            }`}
          >
            <span className="text-sm font-medium text-text-primary block">{m.label}</span>
            {m.description ? (
              <span className="text-xs text-text-muted block mt-0.5 line-clamp-2">
                {m.description}
              </span>
            ) : null}
          </button>
        ))}
      </div>
      <div className="flex justify-between">
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← {t('provider:suggest_step_region')}
        </Button>
      </div>
    </div>
  );
}

function StepChange({
  modification,
  existing,
  proposedIntensity,
  setProposedIntensity,
  proposedParams,
  setProposedParams,
  onBack,
  onNext,
}: {
  modification: ModificationDefinition;
  existing: ExistingMod | null;
  proposedIntensity: 'light' | 'medium' | 'strong' | undefined;
  setProposedIntensity: (i: 'light' | 'medium' | 'strong' | undefined) => void;
  proposedParams: Record<string, unknown>;
  setProposedParams: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  onBack: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation(['provider']);
  const params = modification.parameters ?? [];

  return (
    <div className="space-y-5">
      <div className="rounded-md border border-border-subtle bg-sand-50 p-4">
        <p className="text-sm font-medium text-text-primary">{modification.label}</p>
        {modification.description ? (
          <p className="text-xs text-text-muted mt-1">{modification.description}</p>
        ) : null}
      </div>

      {modification.supportsIntensity !== false ? (
        <div>
          <p className="text-xs uppercase tracking-wide text-text-muted">Intensity</p>
          <div className="flex gap-2 mt-1.5">
            {(['light', 'medium', 'strong'] as const).map((i) => (
              <button
                key={i}
                type="button"
                onClick={() => setProposedIntensity(i)}
                className={`flex-1 rounded-md border px-3 py-1.5 text-sm capitalize ${
                  proposedIntensity === i
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-border-subtle text-text-secondary hover:bg-sand-50'
                }`}
              >
                {i}
              </button>
            ))}
          </div>
          {existing?.intensity ? (
            <p className="text-xs text-text-muted mt-1">
              {t('provider:suggest_current_value')}: {existing.intensity}
            </p>
          ) : null}
        </div>
      ) : null}

      {params.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {params.map((p) => (
            <ParamRow
              key={p.key}
              parameter={p}
              currentValue={existing?.parameters?.[p.key]}
              proposedValue={proposedParams[p.key]}
              setProposedValue={(v) =>
                setProposedParams((prev) => ({ ...prev, [p.key]: v }))
              }
            />
          ))}
        </div>
      ) : null}

      <div className="flex justify-between">
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← {t('provider:suggest_step_modification')}
        </Button>
        <Button size="sm" onClick={onNext}>
          {t('provider:suggest_step_reason')} →
        </Button>
      </div>
    </div>
  );
}

function ParamRow({
  parameter,
  currentValue,
  proposedValue,
  setProposedValue,
}: {
  parameter: NonNullable<ModificationDefinition['parameters']>[number];
  currentValue: unknown;
  proposedValue: unknown;
  setProposedValue: (v: unknown) => void;
}) {
  const { t } = useTranslation(['provider']);
  return (
    <div className="rounded-md border border-border-subtle p-3">
      <p className="text-xs uppercase tracking-wide text-text-muted">{parameter.label}</p>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-text-muted">
            {t('provider:suggest_current_value')}
          </p>
          <p className="text-sm font-mono text-text-secondary">
            {currentValue == null || currentValue === ''
              ? '-'
              : `${currentValue}${parameter.unit ?? ''}`}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-text-muted">
            {t('provider:suggest_proposed_value')}
          </p>
          {parameter.type === 'select' ? (
            <select
              className="w-full rounded-md border border-border-default bg-surface-elevated px-2 py-1 text-sm"
              value={(proposedValue as string | undefined) ?? ''}
              onChange={(e) => setProposedValue(e.target.value || undefined)}
            >
              <option value="">-</option>
              {(parameter.options ?? []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="number"
              min={parameter.min}
              max={parameter.max}
              step={parameter.step}
              value={(proposedValue as number | undefined) ?? ''}
              onChange={(e) =>
                setProposedValue(e.target.value === '' ? undefined : Number(e.target.value))
              }
              className="w-full rounded-md border border-border-default bg-surface-elevated px-2 py-1 text-sm"
              placeholder={parameter.unit ?? ''}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function StepReason({
  reason,
  setReason,
  onBack,
  onSubmit,
  pending,
  error,
}: {
  reason: string;
  setReason: (s: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  pending: boolean;
  error: string | null;
}) {
  const { t } = useTranslation(['provider']);
  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs uppercase tracking-wide text-text-muted">
          {t('provider:suggest_reason_label')}
        </label>
        <Textarea
          rows={5}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('provider:suggest_reason_placeholder')}
          className="mt-1"
          data-testid="provider-suggest-reason"
        />
      </div>
      {error ? (
        <Alert tone="error" title="Could not send suggestion">
          {error}
        </Alert>
      ) : null}
      <div className="flex justify-between">
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← {t('provider:suggest_step_change')}
        </Button>
        <Button
          size="sm"
          onClick={onSubmit}
          disabled={!reason.trim() || pending}
          data-testid="provider-suggest-submit"
        >
          {pending ? 'Sending…' : t('provider:suggest_submit')}
        </Button>
      </div>
    </div>
  );
}
